import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";
import MainCard from "components/MainCard";
import {
  Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle,
  Grid, Stack, TextField, Typography,
  Alert, Divider,
} from "@mui/material";
import {
  CheckCircleOutlined,
  CloseCircleOutlined, EditOutlined, StopOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import PageHeader from "components/PageHeader";
import TableStatusChip from "components/table/TableStatusChip";
import ApprovalStatusChip from "components/table/ApprovalStatusChip";
import { approvalChipSx } from "components/table/tableChipStyles";
import StarIcon from "@mui/icons-material/Star";
import { brand } from "utils/designTokens";

function InfoRow({ label, value }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      sx={{ py: 1.1, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        component="span"
        variant="body2"
        fontWeight={500}
        textAlign="right"
        sx={{ wordBreak: "break-word", maxWidth: "60%" }}
      >
        {value ?? "—"}
      </Typography>
    </Stack>
  );
}

export default function DriverView() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [driver,     setDriver]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [actionNote, setActionNote] = useState("");
  const [dialog,     setDialog]     = useState({ open: false, type: "" });
  const [actioning,  setActioning]  = useState(false);
  const [error,      setError]      = useState("");

  useEffect(() => { fetchDriver(); }, [id]);

  const fetchDriver = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/driver/${id}`);
      setDriver(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load driver");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    const { type } = dialog;
    if (type === "reject" && !actionNote.trim()) {
      setError("Rejection reason is required");
      return;
    }
    setActioning(true);
    setError("");
    try {
      if (type === "approve") {
        await api.patch(`/admin/${id}/approve`);
      } else if (type === "reject") {
        await api.patch(`/admin/${id}/reject`, { note: actionNote.trim() });
      } else if (type === "suspend") {
        await api.patch(`/admin/${id}/suspend`, { note: actionNote.trim() || undefined });
      }
      setDialog({ open: false, type: "" });
      setActionNote("");
      await fetchDriver();
    } catch (err) {
      setError(err.response?.data?.message || `${type} failed. Please try again.`);
    } finally {
      setActioning(false);
    }
  };

  const openDialog = (type) => {
    setActionNote("");
    setError("");
    setDialog({ open: true, type });
  };

  const closeDialog = () => {
    if (actioning) return;
    setDialog({ open: false, type: "" });
    setActionNote("");
    setError("");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!driver) {
    return (
      <Box sx={{ textAlign: "center", p: 6 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {error || "Driver not found"}
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/driver/list")}>
          Back to List
        </Button>
      </Box>
    );
  }

  const profileImg  = driver.profileImage?.imageUrl;
  const docImg      = driver.document?.imageUrl;
  const status      = driver.approval_status;
  const showApprove = status !== "approved";
  const showReject  = status !== "rejected";
  const showSuspend = status !== "suspended";

  const brandBtnSx = {
    minWidth: 110,
    bgcolor: brand.primary,
    boxShadow: 'none',
    '&:hover': { bgcolor: brand.primaryDark, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: 'rgba(31, 91, 64, 0.45)', color: '#fff' }
  };

  const dialogConfig = {
    approve: {
      title: "Approve Driver",
      desc: `Approve ${driver.display_name}? They will be able to log in and receive delivery orders.`,
      btnLabel: "Approve",
      btnSx: brandBtnSx
    },
    reject: {
      title: "Reject Driver",
      desc: `Reject ${driver.display_name}? Please provide a reason — this will be visible to the driver.`,
      btnLabel: "Reject",
      btnSx: brandBtnSx
    },
    suspend: {
      title: "Suspend Driver",
      desc: `Suspend ${driver.display_name}? They won't be able to accept new orders until reinstated.`,
      btnLabel: "Suspend",
      btnSx: brandBtnSx
    }
  };

  const dlgCfg = dialogConfig[dialog.type] || {};

  return (
    <Box>
      <PageHeader
        variant="nested"
        onBack={() => navigate(-1)}
        backTitle="Back to drivers"
        title="Driver Details"
        subtitle={`ID: ${driver.id}`}
        actions={
          <Button
            variant="outlined"
            startIcon={<EditOutlined />}
            onClick={() => navigate(`/driver/edit/${driver.id}`)}
            sx={{ textTransform: 'none', borderRadius: '999px', px: 2 }}
          >
            Edit
          </Button>
        }
      />

      <Grid container spacing={2.5}>

        {/* ── LEFT column ─────────────────────────────────────────────── */}
        <Grid size={{xs:12, md:4}}>
          <MainCard sx={{ textAlign: "center" }}>
            <Avatar
              src={profileImg}
              sx={{ width: 90, height: 90, bgcolor: "primary.lighter", fontSize: 34, mx: "auto", mb: 1.5 }}
            >
              {!profileImg && (driver.display_name || "D")[0].toUpperCase()}
            </Avatar>

            <Typography variant="h6" fontWeight={600}>{driver.display_name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {driver.user?.name}
            </Typography>

            {driver.store ? (
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.8,
                bgcolor: "secondary.lighter", px: 1.5, py: 0.5, borderRadius: 1.5, mb: 1.5,
              }}>
                <ShopOutlined style={{ fontSize: 13 }} />
                <Typography variant="caption" fontWeight={500}>
                  {driver.store.title_en} ({driver.store.store_code})
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 1.5 }}>
                No store assigned
              </Typography>
            )}

            <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="center" sx={{ mb: 2 }}>
              <ApprovalStatusChip status={driver.approval_status} />
              <TableStatusChip active={!!driver.status} />
              <Chip
                label={driver.is_online ? "Online" : "Offline"}
                size="small"
                sx={approvalChipSx(driver.is_online ? 'approved' : 'suspended')}
              />
            </Stack>

            <Box sx={{ bgcolor: "primary.lighter", borderRadius: 2, p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <StarIcon sx={{ fontSize: 28, color: brand.primary }} />
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {Number(driver.rating?.current_rating || 0).toFixed(1)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {driver.rating?.total_count || 0} total reviews
              </Typography>
            </Box>
          </MainCard>

          {docImg && (
            <MainCard title="Driver Document" sx={{ mt: 2 }}>
              <Box
                component="img"
                src={docImg}
                alt="Document"
                sx={{ width: "100%", borderRadius: 1, objectFit: "cover" }}
              />
            </MainCard>
          )}
        </Grid>

        {/* ── RIGHT column ────────────────────────────────────────────── */}
        <Grid size={{xs:12, md:8}}>

          {/* Account Information */}
          <MainCard title="Account Information">
            <InfoRow label="Full Name"    value={driver.user?.name} />
            <InfoRow label="Username"     value={driver.user?.username ? `@${driver.user.username}` : null} />
            <InfoRow label="Phone"        value={driver.user?.phone} />
            <InfoRow label="Email"        value={driver.user?.email} />
            <InfoRow label="Display Name" value={driver.display_name} />
            <InfoRow
              label="Store"
              value={driver.store ? `${driver.store.title_en} (${driver.store.store_code})` : "Not assigned"}
            />
            {driver.latitude && (
              <InfoRow
                label="Location"
                value={`${Number(driver.latitude).toFixed(5)}, ${Number(driver.longitude).toFixed(5)}`}
              />
            )}
            <InfoRow
              label="Registered"
              value={new Date(driver.created_at).toLocaleDateString("en-IN", {
                day: "numeric", month: "short", year: "numeric",
              })}
            />
            {driver.approval_note && (
              <InfoRow label="Approval Note" value={driver.approval_note} />
            )}
          </MainCard>

          {/* ── Approved: show success banner only, no action card ─────── */}
          {status === "approved" && (
            <MainCard sx={{ mt: 2.5 }}>
              <Alert severity="success" icon={<CheckCircleOutlined />} sx={{ borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  Driver is approved and active
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  They can log in and receive delivery orders.
                </Typography>
              </Alert>
            </MainCard>
          )}

          {/* ── Not approved: show action buttons ─────────────────────── */}
          {status !== "approved" && (
            <MainCard title="Approval Actions" sx={{ mt: 2.5 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                  {error}
                </Alert>
              )}

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Current status:{" "}
                    <strong style={{ textTransform: "capitalize" }}>{driver.approval_status}</strong>
                  </Typography>
                  {driver.approval_note && (
                    <Typography variant="caption" color="text.secondary">
                      Note: {driver.approval_note}
                    </Typography>
                  )}
                </Box>
                <ApprovalStatusChip status={driver.approval_status} />
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Stack direction="row" gap={1.5} flexWrap="wrap">
                {showApprove && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutlined />}
                    onClick={() => openDialog("approve")}
                    sx={{ bgcolor: '#1f5b40', '&:hover': { bgcolor: '#133a28' } }}
                  >
                    Approve
                  </Button>
                )}
                {showReject && (
                  <Button
                    variant="outlined"
                    startIcon={<CloseCircleOutlined />}
                    onClick={() => openDialog("reject")}
                    sx={{ borderColor: 'rgba(198, 40, 40, 0.4)', color: '#b71c1c' }}
                  >
                    Reject
                  </Button>
                )}
                {showSuspend && (
                  <Button
                    variant="outlined"
                    startIcon={<StopOutlined />}
                    onClick={() => openDialog("suspend")}
                    sx={{ borderColor: 'rgba(31, 91, 64, 0.3)', color: '#1f5b40' }}
                  >
                    Suspend
                  </Button>
                )}
              </Stack>
            </MainCard>
          )}

        </Grid>
      </Grid>

      {/* ── Confirmation Dialog ──────────────────────────────────────── */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{dlgCfg.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{dlgCfg.desc}</DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          {(dialog.type === "reject" || dialog.type === "suspend") && (
            <TextField
              fullWidth
              multiline
              rows={3}
              required={dialog.type === "reject"}
              label={dialog.type === "reject" ? "Rejection Reason *" : "Note (optional)"}
              value={actionNote}
              onChange={(e) => { setActionNote(e.target.value); setError(""); }}
              placeholder="Enter reason…"
              helperText={
                dialog.type === "reject"
                  ? "This reason will be shown to the driver"
                  : "Optional — reason for suspension"
              }
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={closeDialog} disabled={actioning} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleAction}
            variant="contained"
            color="primary"
            disabled={actioning || (dialog.type === "reject" && !actionNote.trim())}
            startIcon={actioning ? <CircularProgress size={16} color="inherit" /> : null}
            sx={dlgCfg.btnSx}
          >
            {actioning ? "Processing…" : dlgCfg.btnLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}